<template>
  <div>
    <div v-if="errored" class="errored">Une erreur s'est produite 😥</div>

    <div v-else-if="loaded" id="cool-rtl" :class="'resume ' + person.lang">
      <div class="banner">
        <img v-if="person.picture" :src="person.picture" class="profile" />
        <div>
          <h1>{{ person.name.first }} {{ person.name.last }}</h1>
          <span
            >{{ person.position }}<span v-if="person.birth.year"> — {{ lang.born }} {{ person.birth.year }}</span></span
          >
        </div>
      </div>

      <div class="container">
        <div class="content-left">
          <section v-if="person.experience.length" class="experience">
            <h2><i class="material-icons">work</i>{{ lang.experience }}</h2>
            <div v-for="experience in person.experience" class="item">
              <h3>
                {{ experience.position }}<br />
                <span class="company">{{ experience.company }}</span
                ><br />
                <span class="timeperiod">{{ experience.timeperiod_from }} - {{ experience.timeperiod_to || lang.today }}</span>
              </h3>
              <div v-if="experience.description" class="description">{{ experience.description }}</div>
            </div>
          </section>

          <section v-if="person.education.length" class="education">
            <h2><i class="material-icons">school</i>{{ lang.education }}</h2>
            <div v-for="education in person.education" class="item">
              <h3>
                {{ education.degree_role }}<br />
                <span class="company">{{ education.degree_structure }}</span
                ><br />
                <span class="timeperiod">{{ education.timeperiod }}</span>
              </h3>
              <div v-if="education.description" class="description">{{ education.description }}</div>
            </div>
          </section>

          <section v-if="person.projects.length" class="projects">
            <h2><i class="material-icons">code</i>{{ lang.projects }}</h2>
            <div v-for="project in person.projects" class="item">
              <h3>
                {{ project.name }}<br />
                <span class="company">{{ project.platform }}</span
                ><br />
                <span class="timeperiod">{{ project.timeperiod_from }} - {{ project.timeperiod_to }}</span>
              </h3>
              <div v-if="project.description" class="description">{{ project.description }}</div>
            </div>
          </section>
        </div>

        <div class="content-right">
          <section v-if="person.about" class="about">
            <h2>{{ lang.about }}</h2>
            <div class="description">{{ person.about }}</div>
          </section>

          <section class="skills">
            <h2>{{ lang.skills }}</h2>
            <ul>
              <li v-for="skill in person.skills">
                <span>{{ skill.name }}</span>
                <span class="skill-bar">
                  <span :style="'width: ' + skill.level + '%'" class="level"></span>
                </span>
              </li>
            </ul>
          </section>

          <section class="contact">
            <h2>{{ lang.contact }}</h2>
            <ul>
              <li v-if="person.contact.street">
                <i class="fa fa-home"></i>
                {{ person.contact.street }}, {{ person.contact.city }}
              </li>
              <li>
                <i class="fa fa-envelope"></i>
                <a :href="contactLinks.email">{{ person.contact.email }}</a>
              </li>
              <li>
                <i class="fa fa-phone"></i>
                <a :href="'tel:' + person.contact.phone">{{ person.contact.phone }}</a>
              </li>
              <li v-if="person.contact.website">
                <i class="fa fa-globe"></i>
                <a :href="person.contact.website" target="_blank">{{ person.contact.website }}</a>
              </li>
              <li v-if="person.contact.github">
                <i class="fa fa-github"></i>
                <a :href="contactLinks.github" target="_blank">{{ person.contact.github }}</a>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
    <div v-else class="loading">Chargement en cours ⏰</div>
  </div>
</template>

<script>
import Vue from 'vue';
import { getVueOptions } from './options';

const name = 'cool-rtl';
export default Vue.component(name, getVueOptions(name));
</script>

<style lang="less" scoped>
@page cool-rtl {
  margin: 0;
}

@primary-color: #4b5563;
@secondary-color: #10b981;
@third-color: #fff;
@normal: 11pt;
@big: 20pt;
@medium: 15pt;
@small: 10pt;

#cool-rtl {
  position: relative;
  color: @primary-color;
  font-family: 'Brown', sans-serif;
  font-weight: 300;
  font-size: @normal;
  line-height: 17pt;
  page: cool-rtl;
  .ar& {
    direction: rtl;
    font-family: 'Noto Sans Arabic', 'Brown', sans-serif;
    font-weight: 400;
  }

  .banner {
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: 0.5cm;
    align-items: center;
    padding: 2cm 1cm;
    background-color: @secondary-color;
    color: @third-color;

    .profile {
      border: 5px solid @primary-color;
      border-radius: 50%;
      width: 128px;
      height: 128px;
      vertical-align: middle;
    }

    h1 {
      font-size: @big;
      text-transform: uppercase;
      line-height: 1;
      margin: 0;
      .ar& {
        font-weight: 700;
      }
    }
  }

  .container {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));

    .content-left {
      grid-column-start: 1;
      grid-column-end: 8;
      margin-inline-start: 1cm;
      margin-inline-end: 1cm;
    }

    .content-right {
      grid-column-start: 8;
      grid-column-end: 13;
      background-color: @primary-color;
      color: @third-color;
      padding: 0 2cm;
      padding-inline-start: 1cm;
      padding-inline-end: 1cm;
    }
  }

  section {
    padding: 1cm 0 0;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  h2 {
    display: flex;
    align-items: center;
    font-size: @medium;
    text-transform: uppercase;
    margin-top: 0;
    margin-bottom: 0.3cm;
    .ar& {
      font-weight: 700;
    }

    i {
      margin-inline-end: 5px;
    }
  }

  .item:not(:last-child) {
    margin-bottom: 0.3cm;
  }

  h3 {
    font-size: @normal;
    text-transform: uppercase;
    margin: 0;
    font-weight: 400;
    .ar& {
      font-weight: 500;
    }
  }

  .company {
    font-weight: 800;
  }

  .timeperiod {
    text-transform: capitalize;
  }

  .description {
    white-space: pre-line;
  }

  .skills ul {
    margin: 0;
    padding: 0;
    list-style: none;
    line-height: 1.6;

    li {
      display: grid;
      grid-template-columns: max-content 1fr;
      align-items: center;
      column-gap: 1cm;
    }

    .skill-bar {
      display: flex;
      background-color: @third-color;
      height: 5px;
      border-radius: 3px;
      overflow: hidden;
    }

    .level {
      background-color: @secondary-color;
      height: 100%;
    }
  }

  .contact ul {
    margin: 0;
    padding: 0;
    list-style: none;

    a {
      color: @third-color;
      text-decoration: none;
    }
  }
}
</style>
